import {
  Grid,
  Button,
  Text,
  Flex,
  Box,
  ActionIcon,
  useMantineTheme,
  Group,
} from "@mantine/core";
import { TbPentagon } from "react-icons/tb";
import { memo, useContext, useCallback } from "react";
import { CatalogContext } from "src/api/context/CatalogContext";
import { BiMinusCircle, BiPlusCircle } from "react-icons/bi";
import { CartType, formatter } from "src/utils/constants";
import { MdDelete } from "react-icons/md";

function ShoppingCart() {
  const theme = useMantineTheme();
  const { handleCartUpdate, productsCart, handleCheckout, resetState } =
    useContext(CatalogContext);

  const calculateSubTotal = useCallback(() => {
    const totalSum = productsCart.reduce(
      (sum, obj) => sum + obj.count! * obj.cost,
      0
    );
    return formatter.format(totalSum);
  }, [productsCart]);

  return (
    <Grid.Col
      span="auto"
      sx={(theme) => ({
        background: theme.colors.secondary[5],
        padding: "0 10px",
        height: "100%",
      })}
    >
      <Flex
        direction={"column"}
        justify={"space-between"}
        sx={{ height: "100%" }}
      >
        <Box>
          <Group
            p={5}
            w={"100%"}
            position="apart"
            sx={{
              borderBottom: `1px solid #DDDDDD`,
            }}
          >
            <Flex justify={"center"} style={{ flex: 1 }}>
              <Text fw={500} size={20} mb={0}>
                Shopping Cart
              </Text>
            </Flex>
            <Flex style={{ flex: 1 }} justify={"center"}>
              <Button
                radius={"xl"}
                size="xs"
                onClick={resetState}
                leftIcon={<MdDelete size={16} />}
                style={{ maxWidth: 120 }}
              >
                {"Reset Cart"}
              </Button>
            </Flex>
          </Group>

          {productsCart.map((cart) => (
            <Flex
              key={cart.id}
              p={"8px 0"}
              justify={"space-between"}
              align={"center"}
              sx={{
                borderBottom: `1px solid #DDDDDD`,
              }}
            >
              <Flex gap={10}>
                <Box
                  sx={(theme) => ({
                    height: 50,
                    width: 50,
                    background: theme.colors.gray[1],
                    borderRadius: 4,
                  })}
                >
                  {cart?.imgUrl && (
                    <img
                      src={cart.imgUrl}
                      style={{
                        height: "100%",
                        width: "100%",
                        objectFit: "cover",
                      }}
                      alt="Product Image"
                    />
                  )}
                </Box>
                <Flex direction={"column"} gap={5}>
                  <Text size={14}>{cart.name}</Text>
                  <Text size={12} fw={600}>
                    {formatter.format(cart.cost * cart.count)}
                  </Text>
                </Flex>
              </Flex>
              <Flex
                sx={(theme) => ({
                  background: theme.colors.secondary[1],
                  borderRadius: 16,
                })}
                gap={5}
                align={"center"}
                justify={"center"}
                p={"0 3px"}
              >
                <ActionIcon
                  color={"secondaryTextColor"}
                  variant="transparent"
                  onClick={() => handleCartUpdate(cart, CartType.subtract)}
                >
                  <BiMinusCircle size={16} />
                </ActionIcon>
                <Text size={14} fw={500} style={{ minWidth: 16 }}>
                  {cart.count}
                </Text>
                <ActionIcon
                  color={"secondaryTextColor"}
                  variant="transparent"
                  disabled={cart.stock === cart.count}
                  onClick={() =>
                    cart.stock !== cart.count &&
                    handleCartUpdate(cart, CartType.add)
                  }
                >
                  <BiPlusCircle size={16} />
                </ActionIcon>
              </Flex>
            </Flex>
          ))}
        </Box>

        <Box>
          {!!productsCart.length && (
            <Flex
              justify={"space-between"}
              sx={{
                borderBottom: `1px solid #DDDDDD`,
                borderTop: `1px solid #DDDDDD`,
              }}
              p={"5px 0"}
              mb={"xs"}
            >
              <Text fw={600}>SubTotal:</Text>
              <Text fw={600}>{calculateSubTotal()}</Text>
            </Flex>
          )}
          <Button
            fullWidth
            mb={20}
            radius={"xl"}
            size="md"
            onClick={handleCheckout}
            leftIcon={<TbPentagon size={18} />}
            disabled={!productsCart.length}
            sx={{
              "&:hover": {
                backgroundColor: theme.colors.primary[4],
              },
            }}
          >
            CHECKOUT
          </Button>
        </Box>
      </Flex>
    </Grid.Col>
  );
}

export default memo(ShoppingCart);
